"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.concentrationLimitPointerMethods = void 0;
const common_1 = require("@finley-technologies/common");
/**
 * This pointer method is dependent on the number of accounts below a certain APR.
 * i.e., there must be less than 20% of accounts with less than or equal to 20.99% APR
 */
const aprAccountLimitPointer = (params) => {
    const { concentrationReceivables, valueConfig } = params;
    // *** Set up hardcoded values ***
    const baseFieldAprKey = 'purchase_apr';
    const aprLimit = 0.2099; // 20.99%
    // *** Set up recording variables ***
    let totalEligibleExposureLessConcentrationLimitKickouts = 0; // Purely for concentrationAmount
    let totalEligibleReceivableCount = 0;
    // In case there are kickouts, need to only loop through the excluded (target) receivables
    // so storing them upfront here for simplicity later
    const excludedReceivables = [];
    // *** Manage calculations by looping through each receivable ***
    for (const receivable of concentrationReceivables) {
        const { finleyReceivableId, baseFields, concentrationLimitFields, eligibilityFields, } = receivable;
        const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
        // We only care about eligible receivables
        if (!eligibilityFields.isEligible)
            continue;
        const apr = baseFields[baseFieldAprKey];
        (0, common_1.finleyAssert)(typeof apr === 'number', `neoFinancialAtbFinancial1.aprAccountLimit: Incurred unsupported APR base field value [key:${baseFieldAprKey}] [receivableId:${finleyReceivableId}]`);
        if (apr <= aprLimit) {
            excludedReceivables.push(receivable);
        }
        totalEligibleExposureLessConcentrationLimitKickouts += eligibleExposureLessConcentrationLimitKickouts;
        totalEligibleReceivableCount += 1;
    }
    const percentBelowTargetApr = common_1.utils.divideByOrZero(excludedReceivables.length, totalEligibleReceivableCount);
    // There aren't enough accounts below the target APR, so no kickouts needed
    if (percentBelowTargetApr < valueConfig.limit) {
        return {
            kickouts: {},
            calculations: {
                concentrationLimit: valueConfig.limit,
                concentrationAmount: totalEligibleExposureLessConcentrationLimitKickouts,
                concentration: percentBelowTargetApr,
            },
        };
    }
    // *** Manage kickout calculations ***
    let ineligibleAprAccountCount = excludedReceivables.length;
    const kickouts = {};
    // Sort the excluded receivables by the amount of eligible exposure less concentration limit kickouts.
    // This way, we will kickout the receivables with the least amount of exposure first.
    excludedReceivables.sort((a, b) => a.concentrationLimitFields
        .eligibleExposureLessConcentrationLimitKickouts -
        b.concentrationLimitFields.eligibleExposureLessConcentrationLimitKickouts);
    for (const receivable of excludedReceivables) {
        const { finleyReceivableId, concentrationLimitFields } = receivable;
        const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
        // Recording the kickout receivable and amount
        kickouts[finleyReceivableId] = eligibleExposureLessConcentrationLimitKickouts;
        // After recording this kickout, update the latest count of ineligible accounts
        // so we can recalculate the percentage and compare to the limit
        ineligibleAprAccountCount -= 1;
        const newPercentBelowTargetApr = common_1.utils.divideByOrZero(ineligibleAprAccountCount, totalEligibleReceivableCount);
        totalEligibleExposureLessConcentrationLimitKickouts -= eligibleExposureLessConcentrationLimitKickouts;
        // Per CM, value has to be below the limit (equal to does not count)
        // We've now kicked out enough accounts to meet the limit, so we can stop
        if (newPercentBelowTargetApr < valueConfig.limit) {
            break;
        }
    }
    // *** Manage concentration calculation ***
    const concentration = common_1.utils.divideByOrZero(ineligibleAprAccountCount, totalEligibleReceivableCount);
    return {
        kickouts,
        calculations: {
            concentrationLimit: valueConfig.limit,
            concentrationAmount: totalEligibleExposureLessConcentrationLimitKickouts,
            concentration,
        },
    };
};
/**
 * This pointer method is dependent on the number of accounts below a certain credit score.
 * i.e., there must be less than or equal to 5% of accounts with less than 600 credit score
 */
const creditScoreAccountLimitPointer = (params) => {
    const { concentrationReceivables, valueConfig } = params;
    // *** Set up hardcoded values ***
    const baseFieldCreditScoreKey = 'application_credit_score';
    const creditScoreLimit = 600;
    // *** Set up recording variables ***
    let totalEligibleExposureLessConcentrationLimitKickouts = 0; // Purely for concentrationAmount
    let totalEligibleReceivableCount = 0;
    // In case there are kickouts, need to only loop through the excluded (target) receivables
    // so storing them upfront here for simplicity later
    const excludedReceivables = [];
    // *** Manage calculations by looping through each receivable ***
    for (const receivable of concentrationReceivables) {
        const { finleyReceivableId, baseFields, concentrationLimitFields, eligibilityFields, } = receivable;
        const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
        // We only care about eligible receivables
        if (!eligibilityFields.isEligible)
            continue;
        const creditScore = baseFields[baseFieldCreditScoreKey];
        (0, common_1.finleyAssert)(typeof creditScore === 'number', `neoFinancialAtbFinancial1.creditScoreAccountLimitPointer: Incurred unsupported credit score base field value [key:${baseFieldCreditScoreKey}] [receivableId:${finleyReceivableId}]`);
        // Don't include accounts with no credit score
        if (creditScore === 0)
            continue;
        if (creditScore < creditScoreLimit) {
            excludedReceivables.push(receivable);
        }
        totalEligibleExposureLessConcentrationLimitKickouts += eligibleExposureLessConcentrationLimitKickouts;
        totalEligibleReceivableCount += 1;
    }
    const percentBelowTargetCreditScore = common_1.utils.divideByOrZero(excludedReceivables.length, totalEligibleReceivableCount);
    // There aren't enough accounts below the target APR, so no kickouts needed
    if (percentBelowTargetCreditScore < valueConfig.limit) {
        return {
            kickouts: {},
            calculations: {
                concentrationLimit: valueConfig.limit,
                concentrationAmount: totalEligibleExposureLessConcentrationLimitKickouts,
                concentration: percentBelowTargetCreditScore,
            },
        };
    }
    // *** Manage kickout calculations ***
    let ineligibleAprAccountCount = excludedReceivables.length;
    const kickouts = {};
    // Sort the excluded receivables by the amount of eligible exposure less concentration limit kickouts.
    // This way, we will kickout the receivables with the least amount of exposure first.
    excludedReceivables.sort((a, b) => a.concentrationLimitFields
        .eligibleExposureLessConcentrationLimitKickouts -
        b.concentrationLimitFields.eligibleExposureLessConcentrationLimitKickouts);
    for (const receivable of excludedReceivables) {
        const { finleyReceivableId, concentrationLimitFields } = receivable;
        const { eligibleExposureLessConcentrationLimitKickouts, } = concentrationLimitFields;
        // Recording the kickout receivable and amount
        kickouts[finleyReceivableId] = eligibleExposureLessConcentrationLimitKickouts;
        // After recording this kickout, update the latest count of ineligible accounts
        // so we can recalculate the percentage and compare to the limit
        ineligibleAprAccountCount -= 1;
        const newPercentBelowTargetCreditScore = common_1.utils.divideByOrZero(ineligibleAprAccountCount, totalEligibleReceivableCount);
        totalEligibleExposureLessConcentrationLimitKickouts -= eligibleExposureLessConcentrationLimitKickouts;
        // Per CM, value has to be below or equal the limit.
        // Kicked out enough accounts to meet the limit, so can stop
        if (newPercentBelowTargetCreditScore <= valueConfig.limit) {
            break;
        }
    }
    // *** Manage concentration calculation ***
    const concentration = common_1.utils.divideByOrZero(ineligibleAprAccountCount, totalEligibleReceivableCount);
    return {
        kickouts,
        calculations: {
            concentrationLimit: valueConfig.limit,
            concentrationAmount: totalEligibleExposureLessConcentrationLimitKickouts,
            concentration,
        },
    };
};
exports.concentrationLimitPointerMethods = [
    {
        method: 'neoFinancialAtbFinancial1.aprAccountLimit',
        pointerImplementation: aprAccountLimitPointer,
    },
    {
        method: 'neoFinancialAtbFinancial1.creditScoreAccountLimit',
        pointerImplementation: creditScoreAccountLimitPointer,
    },
];
//# sourceMappingURL=concentrationLimitPointerMethods.js.map